import React, { Fragment } from 'react'
import cx from 'classnames'
import { Message } from 'sb-react-intl'

import { Icon } from 'components/_new/ui'
import { Href, HrefProps } from 'components/_new/controls'

import s from './Link.scss'


type Color = 'brand' | 'black' | 'white' | 'muted' | 'gray' | 'gray-30' | 'gray-50' | 'gray-70' | 'gray-80'

export type LinkProps = HrefProps & {
  color?: Color
  message?: string | ReactIntlMessage
  html?: boolean
  withArrow?: boolean
  withUnderline?: boolean
  uppercase?: boolean
  semibold?: boolean
  disabled?: boolean
  styleless?: boolean // disable hardcoded font-size
  ariaLabel?: string
  ariaLabelledBy?: string
  dataTestId?: string
}

const Link: React.FunctionComponent<LinkProps> = (props) => {
  const { children, href, to, toTab, onClick } = props

  const {
    withArrow, withUnderline, color, uppercase, styleless, disabled,
    className, semibold, message, html, dataTestId, ariaLabel, ariaLabelledBy,
    ...linkProps
  } = props

  const hrefProps = {
    ...linkProps,
    'data-testid': dataTestId,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
  }

  const isNotLink = !href && !to && !toTab

  const content = (
    <Fragment>
      {children || <Message text={message} html={html} />}
      {
        withArrow && (
          <Icon className={s.arrow} name="rightArrowSmall" />
        )
      }
    </Fragment>
  )

  const rootClassName = cx(className, {
    [s.link]: !styleless, // TODO remove hardcoded font-size from Link
    [s.disabled]: disabled,
    [s.uppercase]: uppercase,
    [s.withUnderline]: withUnderline,
    [s[color]]: s[color],
    [s.semibold]: semibold,
  })

  if (isNotLink) {
    // disable focus on non-interactive links
    const isClickable = typeof onClick === 'function'
    const role = isClickable ? 'button' : 'presentation'
    const tabIndex = isClickable ? 0 : undefined

    return (
      <span
        className={rootClassName}
        role={role}
        tabIndex={tabIndex}
        onClick={onClick}
        data-testid={dataTestId}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
      >
        {content}
      </span>
    )
  }

  return (
    <Href className={rootClassName} {...hrefProps}>
      {content}
    </Href>
  )
}

Link.defaultProps = {
  withUnderline: true,
  color: 'brand',
}


export default Link
